import { useQuery } from '@tanstack/react-query'
import { Hash, ListFilter } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { registrosApi } from '@/modules/registros/api/registros.api'
import type { CardConfig, TipoAgregacion } from '@/modules/registros/types'

interface DynamicMetricCardProps {
  config: CardConfig
  configId: number
}

export function DynamicMetricCard({ config, configId }: DynamicMetricCardProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['agregacion', configId, config.columna, config.tipo_agregacion, config.valor_filtro],
    queryFn: () =>
      registrosApi.getAgregacion(
        configId,
        config.columna,
        config.tipo_agregacion as TipoAgregacion,
        config.valor_filtro,
      ),
    staleTime: 60000,
  })

  const { data: subtotalData, isLoading: subtotalLoading } = useQuery({
    queryKey: ['agregacion-subtotal', configId, config.subtotal?.columna, config.subtotal?.valor_filtro, config.subtotal?.tipo_agregacion],
    queryFn: () =>
      registrosApi.getAgregacion(configId, config.subtotal!.columna, config.subtotal!.tipo_agregacion ?? 'COUNT', config.subtotal!.valor_filtro),
    enabled: !!config.subtotal,
    staleTime: 60000,
  })

  const { data: subtotal2Data, isLoading: subtotal2Loading } = useQuery({
    queryKey: ['agregacion-subtotal2', configId, config.subtotal2?.columna, config.subtotal2?.valor_filtro, config.subtotal2?.tipo_agregacion],
    queryFn: () =>
      registrosApi.getAgregacion(configId, config.subtotal2!.columna, config.subtotal2!.tipo_agregacion ?? 'COUNT', config.subtotal2!.valor_filtro),
    enabled: !!config.subtotal2,
    staleTime: 60000,
  })

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-32 mt-1" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="border-destructive/50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {config.titulo}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive">Error al cargar</div>
        </CardContent>
      </Card>
    )
  }

  const Icon = config.tipo_agregacion === 'DISTINCT_COUNT' ? ListFilter : Hash
  const valor = data?.valor ?? 0
  const subtotalValor = subtotalData?.valor ?? 0
  const subtotal2Valor = subtotal2Data?.valor ?? 0

  return (
    <Card className="border border-gray-300 dark:border-gray-700" style={{ backgroundImage: 'linear-gradient(to right, rgba(20,184,166,0.15), rgba(20,184,166,0.05) 50%, transparent)' }}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{config.titulo}</CardTitle>
        <Icon className="h-4 w-4 text-teal-500" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {valor.toLocaleString('es-EC')}
        </div>
        {config.subtotal && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {subtotalLoading ? '…' : subtotalValor.toLocaleString('es-EC')}{' '}
            {config.subtotal.label ?? config.subtotal.valor_filtro}
            {!subtotalLoading && valor > 0 && (
              <span className="ml-1 text-teal-600 dark:text-teal-400 font-medium">
                · {((subtotalValor / valor) * 100).toFixed(1)}%
              </span>
            )}
          </p>
        )}
        {config.subtotal2 && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {subtotal2Loading ? '…' : subtotal2Valor.toLocaleString('es-EC')}{' '}
            {config.subtotal2.label ?? config.subtotal2.columna}
            {!subtotal2Loading && valor > 0 && (
              <span className="ml-1 text-teal-600 dark:text-teal-400 font-medium">
                · {((subtotal2Valor / valor) * 100).toFixed(1)}%
              </span>
            )}
          </p>
        )}
        {config.subtitulo && (
          <p className="text-xs text-muted-foreground">{config.subtitulo}</p>
        )}
      </CardContent>
    </Card>
  )
}
